import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  IconButton,
  TextField,
  Box,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckIcon from "@mui/icons-material/Check";
import ScrollView from "devextreme-react/scroll-view";
import { baseAxios } from "../../utils/config";
import "./autocomplete.scss";

const PAGE_COUNT_DEFAULT = 20;

const getStylistDisplayName = (stylist) => {
  const fullName = `${stylist?.user_first_name || ""} ${
    stylist?.user_last_name || ""
  }`.trim();
  return fullName || stylist?.user_name || stylist?.name || "";
};

export default function AsyncAutocompleteStylists({
  label = "Select Stylist",
  onChange,
  className,
  disabled = false,
  value,
  pageCount = PAGE_COUNT_DEFAULT,
  selectedLocation = null, // optional: client-side filter only
}) {
  const [openList, setOpenList] = useState(false);
  const [showClear, setShowClear] = useState(false);
  const [text, setText] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const [allStylists, setAllStylists] = useState([]);
  const [filteredStylists, setFilteredStylists] = useState([]);

  const [endPage, setEndPage] = useState(1);
  const [startPage, setStartPage] = useState(0);

  const listRef = useRef(null);
  const textRef = useRef(null);
  const containerRef = useRef(null);

  const selectedId = value?.user_id ?? value?.id ?? null;

  useEffect(() => {
    if (!openList) return;
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      try {
        const res = await baseAxios.get("/stylists", {
          signal: controller.signal,
        });
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.stylists || res?.data?.data || [];
        const normalized = (list || []).filter(Boolean);
        normalized.sort((a, b) =>
          String(a?.user_name || a?.name || "").localeCompare(
            String(b?.user_name || b?.name || "")
          )
        );
        setAllStylists(normalized);
      } catch (e) {
        if (controller.signal.aborted) return;
        // eslint-disable-next-line no-console
        console.error("Failed to load stylists:", e);
        setAllStylists([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [openList]);

  const baseList = useMemo(() => {
    const locId = selectedLocation?.location_id;
    if (!locId) return allStylists;
    return allStylists.filter((s) => s?.location_id === locId);
  }, [allStylists, selectedLocation]);

  useEffect(() => {
    const q = text.replace(/\s+/g, "").toLowerCase();
    if (!q) {
      setFilteredStylists(baseList);
      setStartPage(0);
      setEndPage(1);
      return;
    }
    const filtered = baseList.filter((s) => {
      const firstName = String(s?.user_first_name || "").replace(/\s+/g, "").toLowerCase();
      const lastName = String(s?.user_last_name || "").replace(/\s+/g, "").toLowerCase();
      const fullName = firstName + lastName;
      const email = String(s?.email || s?.user_email || "").replace(/\s+/g, "").toLowerCase();
      return (
        firstName.includes(q) ||
        lastName.includes(q) ||
        fullName.includes(q) ||
        email.includes(q)
      );
    });
    setFilteredStylists(filtered);
    setStartPage(0);
    setEndPage(1);
  }, [text, baseList]);

  const clipData = useMemo(() => {
    return filteredStylists.filter(
      (_, index) =>
        index < endPage * pageCount && index >= startPage * pageCount
    );
  }, [filteredStylists, endPage, startPage, pageCount]);

  useEffect(() => {
    if (value) {
      setText(getStylistDisplayName(value));
      setShowClear(true);
      setError(false);
    } else {
      setText("");
      setShowClear(false);
    }
  }, [value]);

  const resetList = () => {
    setStartPage(0);
    setEndPage(1);
    setOpenList(false);
    setError(false);
  };

  const checkValidation = () => {
    if (value) {
      setError(false);
      setOpenList(false);
    } else {
      resetList();
      setError(true);
    }
  };

  const handleBlur = (e) => {
    const relatedTarget = e.relatedTarget || document.activeElement;
    const autocompleteList = listRef.current?.instance?.element?.();
    if (autocompleteList && autocompleteList.contains(relatedTarget)) return;
    checkValidation();
  };

  const handleScroll = (e) => {
    const scrollViewInstance = listRef.current?.instance;
    if (!scrollViewInstance) return;
    if (
      scrollViewInstance.scrollHeight() ===
      scrollViewInstance.clientHeight() + e.component.scrollTop()
    ) {
      if (endPage * pageCount >= filteredStylists.length) return;
      setEndPage((prev) => prev + 1);
    }

    if (e.component.scrollTop() === 0) {
      if (startPage === 0) return;
      setStartPage((prev) => Math.max(1, prev - 1));
    }
  };

  const selectStylist = (stylist) => {
    setText(getStylistDisplayName(stylist));
    setShowClear(true);
    setError(false);
    if (onChange) onChange(stylist);
    setTimeout(() => setOpenList(false), 100);
  };

  const handleChange = (e) => {
    const entered = e.target.value;
    setText(entered);
    setShowClear(entered !== "");
    if (onChange) onChange(null);
  };

  return (
    <div
      ref={containerRef}
      className={`async-autocomplete-container ${className} ${
        disabled ? "disabled-box" : ""
      }`}
    >
      {error && !value && (
        <Typography className="error" variant="body2">
          Please select one of {label}.
        </Typography>
      )}
      <TextField
        className={`autocomplete-textfield ${value ? "success" : ""}`}
        size="small"
        variant="outlined"
        label={label}
        value={text}
        error={error && !value}
        inputRef={textRef}
        onFocus={() => setOpenList(true)}
        onBlur={handleBlur}
        disabled={disabled}
        onChange={handleChange}
      />
      <Box className="button-box">
        {showClear && (
          <IconButton
            className="iconbtn"
            size="small"
            aria-label="close"
            disabled={disabled}
            onClick={() => {
              setText("");
              setShowClear(false);
              resetList();
              if (onChange) onChange(null);
              textRef.current?.focus();
            }}
          >
            <CloseIcon className="icon" />
          </IconButton>
        )}
        <IconButton
          className="iconbtn"
          size="small"
          aria-label="open"
          disabled={disabled}
          onClick={() => setOpenList(!openList)}
        >
          <KeyboardArrowDownIcon className="icon" />
        </IconButton>
      </Box>

      {openList && (
        <Box
          className="scheduler-dropdown"
          sx={{
            position: "fixed",
            width: containerRef.current?.offsetWidth || 300,
            bgcolor: "background.paper",
            boxShadow: 4,
            borderRadius: 1,
            border: "1px solid #bbbbbb",
          }}
          style={{
            top: containerRef.current
              ? containerRef.current.getBoundingClientRect().bottom + 2
              : 0,
            left: containerRef.current
              ? containerRef.current.getBoundingClientRect().left
              : 0,
          }}
        >
          <ScrollView
            ref={listRef}
            className="autocomplete-list-fixed"
            height={220}
            style={{
              zIndex: 10,
            }}
            onScroll={handleScroll}
          >
            <List component="nav">
              {loading && clipData.length === 0 && (
                <ListItemButton disabled className="list-item">
                  <ListItemText
                    className="item-text"
                    primary="Loading stylists…"
                  />
                  <CircularProgress size={16} />
                </ListItemButton>
              )}
              {clipData.map((s) => {
                const id = s?.user_id ?? s?.id;
                const isSelected = selectedId != null && id === selectedId;
                const userName = (s?.user_first_name || "-") + " " + (s?.user_last_name || "");
                const userEmail = s?.user_email || s?.email || "-";
                
                return (
                  <ListItemButton
                    key={String(id)}
                    className="list-item"
                    sx={{ justifyContent: "space-between" }}
                    selected={isSelected}
                    disabled={disabled}
                    onClick={() => selectStylist(s)}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "4px 0",
                      }}
                    >
                      <div style={{ fontWeight: "bold", fontSize: "14px" }}>{userName}</div>
                      <div className="dropdown-secondary" style={{ fontSize: "12px", color: "#666" }}>{userEmail}</div>
                    </div>
                    {isSelected && <CheckIcon className="icon-check" />}
                  </ListItemButton>
                );
              })}
              {!loading && clipData.length === 0 && (
                <ListItemButton disabled className="list-item">
                  <ListItemText
                    className="item-text no-data"
                    primary="No Stylists Found"
                    secondary="Please search by other keywords"
                  />
                </ListItemButton>
              )}
            </List>
          </ScrollView>
        </Box>
      )}
    </div>
  );
}
